import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { Dispatch, RootState } from "shared/store/store";
import { useSelector } from "react-redux";
import { fieldsFormCostCenter } from "../helpers/fieldsFormCostCenter";
import { columnsCostCenter } from "../helpers/columnsCostCenter";
import { ICostCenter } from "../models/ICostCenter";
import { ModalCostCenter } from "../components/ModalCostCenter";
import {
  postCostCenter,
  PostCostCenterRequest,
} from "shared/api/services/postCostCenter";
import {
  getCostCenter,
  GetCostCenterParams,
} from "shared/api/services/getCostCenter";
import { getCostCenterStates } from "shared/api/services/getCostCenterStates";
import {
  putCostCenter,
  PutCostCenterRequest,
} from "shared/api/services/putCostCenter";
import { deleteCostCenter } from "shared/api/services/deleteCostCenter";
import Swal from "sweetalert2";

interface UseCostCenterResult {
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
  columns: any[];
  valuesResultsPages: number[];
  showForm: boolean;
  dataFields: any[];
  getValue: (name: string) => string;
  toogleShowForm: () => void;
  createCostCenter: () => void;
  clearFilters: () => void;
}

export const useCostCenter = (): UseCostCenterResult => {
  const dispatch = useDispatch<Dispatch>();
  const { content, paramsUrl, url, totalElements, number, size } = useSelector(
    (state: RootState) => state.electronicDocuments,
  );

  const [showForm, setShowForm] = useState(false);
  const [stateOptions, setStateOptions] = useState<any[]>([]);
  const valuesResultsPages = useMemo(() => [50, 100, 250, 500], []);
  const dataFields = useMemo(() => fieldsFormCostCenter(), []);

  const toogleShowForm = () => setShowForm((prev) => !prev);

  useEffect(() => {
    const fetchStateOptions = async () => {
      try {
        const states = await getCostCenterStates();
        setStateOptions(states);
      } catch (error) {
        console.error("Error fetching cost center states:", error);
      }
    };

    fetchStateOptions();
  }, []);

  const { handleChange, handleSubmit, values, resetForm } = useFormik({
    initialValues: fieldsFormCostCenter().reduce<Record<string, string>>(
      (acc, field) => {
        acc[field.name] = "";
        return acc;
      },
      {},
    ),
    onSubmit: async () => {
      const params: GetCostCenterParams = { page: 0, size: 50 };
      if (values.codigoCentroCostos) params.search = values.codigoCentroCostos;
      else if (values.codigoConcepto) params.search = values.codigoConcepto;

      try {
        const data = await getCostCenter(params);

        const content = data.content.map((item) => ({
          id: item.id,
          codigoCentroCostos: item.code,
          nombre: item.name,
          descripcionActividadProyecto: item.description,
          codigoConcepto: item.conceptCode,
          cuentaContable: item.accountingAccount,
          fuenteFinanciacion: item.fundingSource,
          inicioVigencia: item.effectiveStartDate ?? "",
          finVigencia: item.effectiveEndDate ?? "",
          estado: item.state?.id ?? 1,
        }));

        dispatch.electronicDocuments.cleanState();
        dispatch.electronicDocuments.addState({
          paramsUrl: { page: 0, size: 50, ...params },
          url: "/v1/cost-centers",
          res: {
            content,
            totalElements: data.totalElements,
            number: data.number,
            size: data.size,
            contentSize: data.contentSize,
          },
        });
      } catch (error) {
        console.error("Error filtering cost centers:", error);
      }
    },
    validateOnChange: false,
    enableReinitialize: true,
  });

  const getValue = (name: string) => {
    const valueField = Object.entries(values).find(([key]) => key === name);
    return valueField ? (valueField[1] as string) : "";
  };

  const clearFilters = async () => {
    resetForm();
    try {
      const data = await getCostCenter({ page: 0, size: 50 });

      const content = data.content.map((item) => ({
        id: item.id,
        codigoCentroCostos: item.code,
        nombre: item.name,
        descripcionActividadProyecto: item.description,
        codigoConcepto: item.conceptCode,
        cuentaContable: item.accountingAccount,
        fuenteFinanciacion: item.fundingSource,
        inicioVigencia: item.effectiveStartDate ?? "",
        finVigencia: item.effectiveEndDate ?? "",
        estado: item.state?.id ?? 1,
      }));

      dispatch.electronicDocuments.cleanState();
      dispatch.electronicDocuments.addState({
        paramsUrl: { page: 0, size: 50 },
        url: "/v1/cost-centers",
        res: {
          content,
          totalElements: data.totalElements,
          number: data.number,
          size: data.size,
          contentSize: data.contentSize,
        },
      });
    } catch (error) {
      console.error("Error clearing filters:", error);
    }
  };

  const createCostCenter = () => {
    const onClose = () => {
      dispatch.messageModal.cleanStateMessage();
    };

    const handleCreateSubmit = async (values: Record<string, string>) => {
      const payload: PostCostCenterRequest = {
        code: values.codigoCentroCostos,
        name: values.nombre,
        description: values.descripcionActividadProyecto,
        accountingAccount: values.cuentaContable,
        conceptCode: values.codigoConcepto,
        fundingSource: values.fuenteFinanciacion,
        stateId: values.estado ? Number(values.estado) : 1,
        effectiveStartDate: values.inicioVigencia,
        effectiveEndDate: values.finVigencia,
      };

      const response = await postCostCenter(payload);

      const nextRow: ICostCenter & Record<string, any> = {
        id: response.id,
        codigoCentroCostos: response.code,
        nombre: response.name,
        descripcionActividadProyecto: response.description,
        codigoConcepto: response.conceptCode,
        cuentaContable: response.accountingAccount,
        fuenteFinanciacion: response.fundingSource,
        inicioVigencia: response.effectiveStartDate,
        finVigencia: response.effectiveEndDate,
        estado: response.state?.name ?? "Activo",
      };

      const current = (content as any[]) ?? [];
      const next = [nextRow, ...current];

      dispatch.electronicDocuments.addState({
        paramsUrl: paramsUrl || { page: 0, size: 50 },
        url: url || "/v1/cost-centers",
        res: {
          content: next,
          totalElements: totalElements + 1,
          number: number,
          size: size,
          contentSize: next.length,
        },
      });

      Swal.fire({
        icon: "success",
        title: "Centro de costo creado exitosamente",
        confirmButtonText: "Aceptar",
      });

      onClose();
    };

    dispatch.messageModal.setMessageModal({
      show: true,
      title: "Crear centro de costo",
      onClose() {
        onClose();
      },
      size: "large",
      description: React.createElement(ModalCostCenter, {
        onClose,
        onSubmit: handleCreateSubmit,
        stateOptions,
      }),
    });
  };

  const editCostCenter = useCallback(
    (row: ICostCenter & Record<string, any>) => {
      const onClose = () => {
        dispatch.messageModal.cleanStateMessage();
      };

      const handleSubmit = async (values: Record<string, string>) => {
        const payload: PutCostCenterRequest = {
          name: values.nombre,
          description: values.descripcionActividadProyecto,
          accountingAccount: values.cuentaContable,
          conceptCode: values.codigoConcepto,
          fundingSource: values.fuenteFinanciacion,
          stateId: values.estado ? Number(values.estado) : 1,
          effectiveStartDate: values.inicioVigencia,
          effectiveEndDate: values.finVigencia,
        };

        const response = await putCostCenter(row.id!, payload);

        const updatedRow: ICostCenter & Record<string, any> = {
          id: response.id,
          codigoCentroCostos: response.code,
          nombre: response.name,
          descripcionActividadProyecto: response.description,
          codigoConcepto: response.conceptCode,
          cuentaContable: response.accountingAccount,
          fuenteFinanciacion: response.fundingSource,
          inicioVigencia: response.effectiveStartDate,
          finVigencia: response.effectiveEndDate,
          estado: response.state?.name ?? "Activo",
        };

        const current = (content as any[]) ?? [];
        const next = current.map((x) => (x?.id === row?.id ? updatedRow : x));

        dispatch.electronicDocuments.addState({
          paramsUrl: paramsUrl || { page: 0, size: 50 },
          url: url || "/v1/cost-centers",
          res: {
            content: next,
            totalElements: totalElements || 0,
            number: number || 0,
            size: size || 50,
            contentSize: next.length,
          },
        });

        Swal.fire({
          icon: "success",
          title: "Centro de costo actualizado exitosamente",
          confirmButtonText: "Aceptar",
        });

        onClose();
      };

      dispatch.messageModal.setMessageModal({
        show: true,
        title: "Editar centro de costo",
        onClose() {
          onClose();
        },
        size: "large",
        description: React.createElement(ModalCostCenter, {
          onClose,
          costCenter: row,
          onSubmit: handleSubmit,
          stateOptions,
        }),
      });
    },
    [
      dispatch.electronicDocuments,
      dispatch.messageModal,
      stateOptions,
      content,
      paramsUrl,
      url,
      totalElements,
      number,
      size,
    ],
  );

  const inactivateCostCenter = useCallback(
    (row: ICostCenter & Record<string, any>) => {
      if (!content) return;

      Swal.fire({
        title: "¿Está seguro que desea inactivar este centro de costo?",
        icon: "warning",
        showCancelButton: true,
        cancelButtonText: "No",
        confirmButtonText: "Sí",
      })?.then(async (result: any) => {
        if (result.isConfirmed) {
          try {
            await deleteCostCenter(row.id!);
            const next = (content as any[]).filter((x) => x?.id !== row?.id);
            dispatch.electronicDocuments.addState({
              paramsUrl: paramsUrl || { page: 0, size: 50 },
              url: url || "/v1/cost-centers",
              res: {
                content: next,
                totalElements: (totalElements || 0) - 1,
                number: number || 0,
                size: size || 50,
                contentSize: next.length,
              },
            });

            Swal.fire({
              icon: "success",
              title: "Centro de costo inactivado exitosamente",
              confirmButtonText: "Aceptar",
            });
          } catch (error) {
            console.error("Error inactivating cost center:", error);
            Swal.fire({
              icon: "error",
              title: "Error al inactivar el centro de costo",
              confirmButtonText: "Aceptar",
            });
          }
        }
      });
    },
    [
      content,
      dispatch.electronicDocuments,
      paramsUrl,
      url,
      totalElements,
      number,
      size,
    ],
  );

  useEffect(() => {
    return () => {
      resetForm();
    };
  }, [resetForm]);

  const columns = useMemo(
    () => columnsCostCenter(editCostCenter, inactivateCostCenter, stateOptions),
    [inactivateCostCenter, editCostCenter, stateOptions],
  );

  useEffect(() => {
    const fetchCostCenters = async () => {
      try {
        const data = await getCostCenter({ page: 0, size: 50 });

        const content = data.content.map((item) => ({
          id: item.id,
          codigoCentroCostos: item.code,
          nombre: item.name,
          descripcionActividadProyecto: item.description,
          codigoConcepto: item.conceptCode,
          cuentaContable: item.accountingAccount,
          fuenteFinanciacion: item.fundingSource,
          inicioVigencia: item.effectiveStartDate ?? "",
          finVigencia: item.effectiveEndDate ?? "",
          estado: item.state?.id ?? 1,
        }));

        dispatch.electronicDocuments.cleanState();
        dispatch.electronicDocuments.addState({
          paramsUrl: { page: 0, size: 50 },
          url: "/v1/cost-centers",
          res: {
            content,
            totalElements: data.totalElements,
            number: data.number,
            size: data.size,
            contentSize: data.contentSize,
          },
        });
      } catch (error) {
        console.error("Error fetching cost centers:", error);
      }
    };

    fetchCostCenters();
  }, [dispatch.electronicDocuments]);

  useEffect(() => {
    const fetchCostCentersPaginated = async () => {
      if (!url || !url.includes("cost-centers")) return;

      try {
        const params: GetCostCenterParams = {
          page: Number(paramsUrl?.page) || 0,
          size: Number(paramsUrl?.size) || 50,
        };
        if ((paramsUrl as any)?.orderBy)
          params.orderBy = (paramsUrl as any).orderBy;
        if ((paramsUrl as any)?.search)
          params.search = (paramsUrl as any).search;

        const data = await getCostCenter(params);

        const content = data.content.map((item) => ({
          id: item.id,
          codigoCentroCostos: item.code,
          nombre: item.name,
          descripcionActividadProyecto: item.description,
          codigoConcepto: item.conceptCode,
          cuentaContable: item.accountingAccount,
          fuenteFinanciacion: item.fundingSource,
          inicioVigencia: item.effectiveStartDate ?? "",
          finVigencia: item.effectiveEndDate ?? "",
          estado: item.state?.id ?? 1,
        }));

        dispatch.electronicDocuments.addState({
          paramsUrl,
          url,
          res: {
            content,
            totalElements: data.totalElements,
            number: data.number,
            size: data.size,
            contentSize: data.contentSize,
          },
        });
      } catch (error) {
        console.error("Error fetching paginated cost centers:", error);
      }
    };

    fetchCostCentersPaginated();
  }, [paramsUrl, url, dispatch.electronicDocuments]);

  return {
    handleChange,
    handleSubmit,
    columns,
    valuesResultsPages,
    showForm,
    dataFields,
    getValue,
    toogleShowForm,
    createCostCenter,
    clearFilters,
  };
};
